// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: converted
// atomKvsSync voice-catalog cache; caching moved from the queryFns into
// useVoicesList (React side owns the registry).
import type { ApiHttpApiContract } from '@listening/api-contract';
import { ApiClient as ApiClientTag } from '@listening/api-contract/api-client-tag';
import {
  type AudioItemDetail,
  defaultMaleVoice,
  type Voice,
} from '@listening/api-contract/legacy-client-types';
import { SchemaUtils } from '@listening/api-contract/schema-utils';
import {
  VISIBLE_VOICE_DEFS,
  VoiceSummary,
  isHiddenVoice,
  knownVoiceDisplayName,
  knownVoiceModelId,
  type VoiceDef,
} from '@listening/api-contract/schemas';
import { RegistryContext, useAtomSet, useAtomValue } from '@effect/atom-react';
import { useQuery } from '@tanstack/react-query';
import type { HttpApiClient } from 'effect/http-api';
import { Effect, Schema, SchemaTransformation, Struct } from 'effect';
import { use, useEffect } from 'react';
import { atomKvsSync } from '../atoms/kvs';
import { useEq } from './runtime-context';

type ApiClient = HttpApiClient.ForApi<typeof ApiHttpApiContract>;

export type CallApi = <A, E>(
  fn: (api: ApiClient) => Effect.Effect<A, E>,
  options?: { signal?: AbortSignal },
) => Promise<A>;

const VOICES_STALE_TIME_MS = 3 * 24 * 60 * 60 * 1000;

export const voicesQueryKey = ['voices', 'list'] as const;

function deriveGender(modelId: string) {
  return modelId[1] === 'm' ? 'male' : 'female';
}

function toPredefinedVoice(
  voice: (typeof VISIBLE_VOICE_DEFS)[number] &
    Pick<VoiceDef, 'is_free' | 'softness'>,
): Voice {
  return {
    gender: deriveGender(voice.model_id),
    image_url: `/voice-images/${voice.model_id}.webp`,
    is_free: voice.is_free ?? false,
    lang: voice.lang,
    model_id: knownVoiceModelId(voice.model_id),
    name: knownVoiceDisplayName(voice.name),
    sample_url: `/voice-samples/${voice.model_id}.mp3`,
    softness: voice.softness ?? 'moderate',
  };
}

// VISIBLE_VOICE_DEFS, not VOICE_DEFS: hidden voices stay resolvable server-side
// but must never appear in a picker.
export const predefinedVoiceCatalog: Array<Voice> =
  VISIBLE_VOICE_DEFS.map(toPredefinedVoice);

// Rows go through the FULL contract schema (not typeSchema): its encoded
// side is the lenient one — rows persisted before a later-added optionalWith
// field still decode (defaults reapply), instead of one old row fail-opening
// the whole cache. The transform's target is the TYPE-side identity so rows
// are decoded/encoded exactly once, by the union members.
const voiceCatalogCacheStateSchema = Schema.Struct({
  voices: Schema.Array(VoiceSummary),
});
type VoiceCatalogCacheState = typeof voiceCatalogCacheStateSchema.Type;
const emptyVoiceCache: VoiceCatalogCacheState = { voices: [] };

// zustand-persist wrote a `{state, version}` envelope (state possibly
// partial). Decode merges over the empty default; encode writes the bare
// shape. See plan doc §C2.
const partialVoiceCacheSchema = voiceCatalogCacheStateSchema.mapFields(
  Struct.map(Schema.optionalKey),
);
const persistedVoiceCacheSchema = Schema.Union([
  Schema.Struct({ state: partialVoiceCacheSchema, version: Schema.Int }),
  partialVoiceCacheSchema,
]).pipe(
  Schema.decodeTo(
    Schema.toType(voiceCatalogCacheStateSchema),
    SchemaTransformation.transform({
      decode: (stored) => ({
        ...emptyVoiceCache,
        ...('state' in stored ? stored.state : stored),
      }),
      encode: (state) => state,
    }),
  ),
);

// Last-known voice catalog, persisted so the reader can offer voices before
// the list query resolves (or offline). Written by useVoicesList below.
export const voiceCatalogCacheAtom = atomKvsSync({
  key: 'voice-catalog-cache-storage',
  schema: persistedVoiceCacheSchema,
  defaultValue: () => emptyVoiceCache,
});

// Shared-hook voice lists funnel through here, so retired voices are dropped
// here too — a persisted cache or an older server's /voices can still carry
// them long after they leave VISIBLE_VOICE_DEFS. (apps/browser-ext has its own
// useVoicesList and relies on the deployed API instead.)
//
// Returns the input array unchanged when nothing is hidden: this result feeds
// `useMemo` dependency chains (e.g. apps/mobile's voices screen), so a fresh
// array identity on every render would defeat them.
export const withoutHidden = (voices: ReadonlyArray<Voice>) => {
  const visible = voices.filter((v) => !isHiddenVoice(v.model_id || v.name));
  return visible.length === voices.length ? voices : visible;
};

export function resolveVoiceCatalogData(
  remoteVoices: ReadonlyArray<Voice> | undefined,
  cachedVoices: ReadonlyArray<Voice>,
): ReadonlyArray<Voice> {
  if (remoteVoices?.length) return withoutHidden(remoteVoices);
  if (cachedVoices.length > 0) return withoutHidden(cachedVoices);
  return predefinedVoiceCatalog;
}

export function normalizeVoiceCacheKey(voiceIdentifier: string | undefined) {
  return (voiceIdentifier ?? '').trim().toLowerCase();
}

export function voiceCacheKeyForVoice(voice: Voice) {
  return normalizeVoiceCacheKey(voice.model_id || voice.name);
}

export function voiceMatchesIdentifier(
  voice: Voice,
  voiceIdentifier: string | undefined,
) {
  const key = normalizeVoiceCacheKey(voiceIdentifier);
  if (!key) return false;
  return (
    key === voiceCacheKeyForVoice(voice) ||
    key === normalizeVoiceCacheKey(voice.name)
  );
}

// This client resolves `persona-<uuid>` voice ids, so it asks for the
// designed voices too.
const listVoicesRequest = { query: { include: 'personas' } } as const;

// Voice rows decode into Effect `Schema.Class` instances; convert to plain
// objects so SSR dehydrate (seroval) can serialize the QueryClient cache.
export const listVoicesEffect = Effect.flatMap(ApiClientTag, (api) =>
  api.Voices.ListVoices(listVoicesRequest).pipe(
    Effect.map((r) => r.voices.map(SchemaUtils.schemaInstanceToPlain)),
  ),
);

// `callApi` form for route-loader prefetch (`ensureQueryData`), where the
// isomorphic `callApiPromise`/`callApi` runner is what's available. The
// persisted cache is written by useVoicesList when the data renders — the
// loader only warms the query cache.
export const voiceQueryOptions = (callApi: CallApi) => ({
  queryKey: voicesQueryKey,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    callApi((api) => api.Voices.ListVoices(listVoicesRequest), {
      signal,
    }).then((r) => r.voices.map(SchemaUtils.schemaInstanceToPlain)),
  staleTime: VOICES_STALE_TIME_MS,
});

// Shared reader hook (same query key + shape as `voiceQueryOptions`, so a
// loader prefetch warms it). Lives here so start / browser-ext / RN can share
// one voices hook; resolves the runtime via `useEq()` (AppRuntimeProvider).
export const useVoicesList = () => {
  const registry = use(RegistryContext);
  const cachedVoices = useAtomValue(voiceCatalogCacheAtom).voices;
  const setVoiceCache = useAtomSet(voiceCatalogCacheAtom);
  const query = useQuery({
    ...useEq().queryOptions({
      queryKey: voicesQueryKey,
      queryFn: () => listVoicesEffect,
    }),
    staleTime: VOICES_STALE_TIME_MS,
  });
  const remoteVoices = query.data;
  // Persist the last-known catalog (client-only: effects don't run in SSR).
  useEffect(() => {
    if (!remoteVoices?.length) return;
    if (registry.get(voiceCatalogCacheAtom).voices === remoteVoices) return;
    setVoiceCache({ voices: remoteVoices });
  }, [registry, remoteVoices, setVoiceCache]);
  return {
    data: resolveVoiceCatalogData(query.data, cachedVoices),
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isPending: query.isPending,
  };
};

// Pick the best voice for an audio given the user's preference + plan. Pure
// (no runtime/cache deps) and identical across apps, so it lives in the shared
// layer to guarantee cross-app parity: prefer the user's chosen voice (free
// users fall back to free voices), then progressively relax to a same-language
// voice matching gender+softness → gender → language → the preferred default.
export function resolveCurrentVoice(params: {
  audioItemLang: AudioItemDetail['language'] | undefined;
  audioPreferredVoiceName?: string | undefined;
  preferredVoiceName: string | undefined;
  isPremiumUser: boolean;
  voiceOptionsAll: ReadonlyArray<Voice>;
}) {
  const {
    audioItemLang,
    audioPreferredVoiceName,
    preferredVoiceName,
    isPremiumUser,
    voiceOptionsAll,
  } = params;

  const availableVoiceOptions = voiceOptionsAll.filter(
    (v) => v.is_free || isPremiumUser,
  );
  const audioPreferredVoice = voiceOptionsAll.find((v) =>
    voiceMatchesIdentifier(v, audioPreferredVoiceName),
  );
  const preferredVoiceWithDefault =
    audioPreferredVoice ??
    availableVoiceOptions.find((v) =>
      voiceMatchesIdentifier(v, preferredVoiceName),
    ) ??
    defaultMaleVoice;

  if (!audioItemLang || audioItemLang === preferredVoiceWithDefault.lang)
    return preferredVoiceWithDefault;
  const voiceWithLangGenderAndSoftness = availableVoiceOptions.find(
    (v) =>
      v.lang === audioItemLang &&
      v.gender === preferredVoiceWithDefault.gender &&
      v.softness === preferredVoiceWithDefault.softness,
  );
  if (voiceWithLangGenderAndSoftness) return voiceWithLangGenderAndSoftness;

  const voiceWithLangAndGender = availableVoiceOptions.find(
    (v) =>
      v.lang === audioItemLang && v.gender === preferredVoiceWithDefault.gender,
  );
  if (voiceWithLangAndGender) return voiceWithLangAndGender;

  const voiceWithLang = availableVoiceOptions.find(
    (v) => v.lang === audioItemLang,
  );
  return voiceWithLang ?? preferredVoiceWithDefault;
}
